import type { ChangeEvent } from "react";
import { CloseIcon, SearchIcon } from "~/icons";
import { Box } from "../Box/Box";
import styles from "./TextField.module.css";

// For a full list of autocomplete values, see: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
type AutoComplete =
  | "username"
  | "current-password"
  | "new-password"
  | "one-time-code"
  | "email"
  | "given-name"
  | "family-name"
  | "street-address"
  | "country"
  | "country-name"
  | "postal-code"
  | "bday"
  | "bday-day"
  | "bday-month"
  | "bday-year"
  | "sex"
  | "tel"
  | "tel-country-code"
  | "tel-national"
  | "tel-area-code"
  | "tel-local"
  | "off"
  | "on";

interface Props {
  id?: string;
  name?: string;
  value?: string;
  type?: "text" | "password" | "date" | "email";
  autoComplete?: AutoComplete;
  placeholder?: string;
  label?: string;
  critical?: boolean;
  disabled?: boolean;
  search?: boolean; // adds search icon to field
  autoFocus?: boolean;
  onChange?: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  error?: string | null;
  reserveMessageSpace?: boolean;
  inputMode?: "numeric" | "decimal";
  required?: boolean;
  min?: string;
  max?: string;
  ariaLabel?: string;
  centered?: boolean;
  highlightOnFocus?: boolean;
  onClear?: () => void;
  clearVisible?: boolean; // controls visibility of the clear button (use with onClear)
}

export function TextField(props: Props): React.ReactNode {
  const { onChange } = props;
  const handleChange = onChange
    ? (e: ChangeEvent<HTMLInputElement>) => {
        onChange(e.currentTarget.value);
      }
    : undefined;
  return (
    <Box className={props.critical ? styles.critical : ""}>
      {props.label && (
        <label className={styles.label} htmlFor={props.id}>
          {props.label}
        </label>
      )}

      <Box
        display="flex"
        border="standard"
        borderRadius="standard"
        borderColor="neutral-300"
        background="white"
        overflow="hidden"
        className={props.highlightOnFocus ? styles.highlightOnFocus : undefined}
      >
        {props.search && (
          <Box
            padding="2"
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ flex: 1 }}
          >
            <SearchIcon className={styles.searchIcon} />
          </Box>
        )}
        <div style={{ flexGrow: 1 }}>
          <input
            // Max width minus padding (30px), width: 100% causes div to overflow bounds.
            style={{
              width: "calc(100% - 30px)",
              textAlign: props.centered ? "center" : undefined,
            }}
            autoComplete={props.autoComplete}
            id={props.id}
            className={styles.input}
            name={props.name}
            type={props.type}
            value={props.value}
            placeholder={props.placeholder}
            disabled={props.disabled}
            onChange={handleChange}
            onKeyDown={props.onKeyDown}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- Some use cases for this text field require immediate focus
            autoFocus={props.autoFocus}
            inputMode={props.inputMode}
            required={props.required}
            min={props.min}
            max={props.max}
            aria-label={props.ariaLabel}
          />
        </div>
        {props.onClear && (
          <Box
            component="button"
            type="button"
            onClick={props.onClear}
            aria-label="Clear"
            display="flex"
            alignItems="center"
            paddingX="2"
            background="white"
            style={{ visibility: props.clearVisible ? "visible" : "hidden" }}
          >
            <CloseIcon width={18} height={18} />
          </Box>
        )}
      </Box>
      {(props.error || props.reserveMessageSpace) && (
        <p className={`${styles.error} ${props.error ? styles.errorShow : ""}`}>
          {props.error ?? `\u00A0`}
        </p>
      )}
    </Box>
  );
}
